import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  Loader2,
  CreditCard,
  TrendingUp,
  PiggyBank,
  BookOpen,
  Target,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Quiz Questions
const QUESTIONS = [
  {
    id: "q1",
    title: "Debt Situation",
    question: "What best describes your current debt situation?",
    options: [
      { value: "A", label: "I have high-interest debt (credit cards, payday loans) that's overwhelming" },
      { value: "B", label: "I have manageable debt (student loans, car payment, low-rate mortgage)" },
      { value: "C", label: "I'm debt-free but have little to no savings" },
      { value: "D", label: "I'm debt-free with emergency savings ready to invest" }
    ]
  },
  {
    id: "q2",
    title: "Monthly Cash Flow",
    question: "At the end of each month, you typically have...",
    options: [
      { value: "A", label: "Nothing left or going into more debt" },
      { value: "B", label: "A little left ($0-$500) but it disappears quickly" },
      { value: "C", label: "Regular savings ($500-$1,500) with no clear plan" },
      { value: "D", label: "Significant savings ($1,500+) ready to allocate" }
    ]
  },
  {
    id: "q3",
    title: "Primary Financial Goal",
    question: "What's your #1 financial priority right now?",
    options: [
      { value: "A", label: "Eliminate debt as fast as possible" },
      { value: "B", label: "Start investing but I'm a complete beginner" },
      { value: "C", label: "Grow my existing investments and optimize my portfolio" },
      { value: "D", label: "Understand money and finance before making any moves" }
    ]
  },
  {
    id: "q4",
    title: "Financial Knowledge",
    question: "How would you rate your investing knowledge?",
    options: [
      { value: "A", label: "Beginner - I don't understand basic terms like P/E ratio or compound interest" },
      { value: "B", label: "Novice - I understand basics but haven't invested yet" },
      { value: "C", label: "Intermediate - I have investments and understand fundamentals" },
      { value: "D", label: "Advanced - I actively manage my portfolio and research stocks" }
    ]
  },
  {
    id: "q5",
    title: "Financial Confidence",
    question: "When making financial decisions, you feel...",
    options: [
      { value: "A", label: "Paralyzed - I avoid decisions because I'm afraid of mistakes" },
      { value: "B", label: "Uncertain - I make decisions but constantly worry I'm wrong" },
      { value: "C", label: "Cautious but willing - I research thoroughly before acting" },
      { value: "D", label: "Confident - I trust my financial judgment and analysis" }
    ]
  },
  {
    id: "q6",
    title: "Time Horizon & Life Stage",
    question: "What's your timeline for financial goals?",
    options: [
      { value: "A", label: "Immediate (0-6 months) - I have urgent financial needs or upcoming expenses" },
      { value: "B", label: "Short-term (1-3 years) - Saving for specific goals like a house or trip" },
      { value: "C", label: "Medium-term (3-10 years) - Building wealth for future flexibility" },
      { value: "D", label: "Long-term (10+ years) - Retirement and generational wealth" }
    ]
  },
  {
    id: "q7",
    title: "Income Stability",
    question: "How stable is your income and job situation?",
    options: [
      { value: "A", label: "Unstable - Freelance, gig work, or at risk of job loss" },
      { value: "B", label: "Somewhat stable but no emergency fund built yet" },
      { value: "C", label: "Stable with 3-6 months emergency fund saved" },
      { value: "D", label: "Very stable with 6+ months emergency fund" }
    ]
  },
  {
    id: "q8",
    title: "Current Investments",
    question: "Which best describes your investment experience?",
    options: [
      { value: "A", label: "Never invested - don't even have a brokerage account" },
      { value: "B", label: "Only 401(k) or employer plan that I don't actively manage" },
      { value: "C", label: "Have some investments but don't actively manage them" },
      { value: "D", label: "Actively invest and track my portfolio regularly" }
    ]
  },
  {
    id: "q9",
    title: "Spending Awareness",
    question: "How well do you track your spending and subscriptions?",
    options: [
      { value: "A", label: "No idea where my money goes each month" },
      { value: "B", label: "Rough idea but don't track subscriptions or recurring charges" },
      { value: "C", label: "I budget and track most expenses" },
      { value: "D", label: "I track every dollar and optimize subscriptions regularly" }
    ]
  },
  {
    id: "q10",
    title: "Specific Upcoming Needs",
    question: "Do you have any major expenses or goals in the next 12 months?",
    options: [
      { value: "A", label: "Yes - Wedding, home purchase, or large expense ($10K+)" },
      { value: "B", label: "Yes - Trip, car, or medium goal ($3K-$10K)" },
      { value: "C", label: "Maybe - Should probably plan for something" },
      { value: "D", label: "No major expenses planned" }
    ]
  }
];

// Scoring Matrix
const SCORING_MATRIX = {
  q1: {
    A: { debtFree: 15 },
    B: { debtFree: 8, planSave: 2 },
    C: { planSave: 10, learnFinance: 2 },
    D: { startInvesting: 10, buildWealth: 5 }
  },
  q2: {
    A: { debtFree: 12, planSave: 3 },
    B: { planSave: 10, debtFree: 3 },
    C: { startInvesting: 8, buildWealth: 4 },
    D: { buildWealth: 10, startInvesting: 5 }
  },
  q3: {
    A: { debtFree: 15 },
    B: { startInvesting: 15, learnFinance: 3 },
    C: { buildWealth: 15, startInvesting: 3 },
    D: { learnFinance: 15, planSave: 2 }
  },
  q4: {
    A: { learnFinance: 10, startInvesting: 2 },
    B: { startInvesting: 8, learnFinance: 5 },
    C: { buildWealth: 8, startInvesting: 5 },
    D: { buildWealth: 12, startInvesting: 3 }
  },
  q5: {
    A: { learnFinance: 12, planSave: 3 },
    B: { learnFinance: 6, startInvesting: 6 },
    C: { startInvesting: 10, buildWealth: 5 },
    D: { buildWealth: 12, startInvesting: 3 }
  },
  q6: {
    A: { planSave: 12, debtFree: 5 },
    B: { planSave: 15 },
    C: { startInvesting: 10, buildWealth: 5 },
    D: { buildWealth: 15, startInvesting: 5 }
  },
  q7: {
    A: { planSave: 15, debtFree: 3 },
    B: { planSave: 12, debtFree: 3 },
    C: { startInvesting: 10, buildWealth: 5 },
    D: { buildWealth: 10, startInvesting: 5 }
  },
  q8: {
    A: { startInvesting: 10, learnFinance: 5 },
    B: { startInvesting: 8, learnFinance: 6 },
    C: { buildWealth: 10, startInvesting: 3 },
    D: { buildWealth: 15 }
  },
  q9: {
    A: { debtFree: 8, planSave: 10 },
    B: { planSave: 12, debtFree: 5 },
    C: { startInvesting: 8, buildWealth: 5 },
    D: { buildWealth: 10, startInvesting: 5 }
  },
  q10: {
    A: { planSave: 20 },
    B: { planSave: 15 },
    C: { planSave: 5 },
    D: { startInvesting: 3, buildWealth: 3 }
  }
};

// Category Info
const CATEGORY_INFO = {
  debtFree: {
    name: "Get Debt-Free",
    path: "/debt-free",
    icon: CreditCard,
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
    textColor: "text-red-600",
    description: "Focus on eliminating debt and taking control of your finances"
  },
  startInvesting: {
    name: "Start Investing",
    path: "/start-investing",
    icon: TrendingUp,
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-600",
    description: "Begin your investment journey with confidence"
  },
  buildWealth: {
    name: "Build Wealth",
    path: "/build-wealth",
    icon: PiggyBank,
    color: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-50",
    textColor: "text-amber-600",
    description: "Optimize and grow your existing investments"
  },
  learnFinance: {
    name: "Learn Finance",
    path: "/learn-finance",
    icon: BookOpen,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-600",
    description: "Build your financial knowledge foundation"
  },
  planSave: {
    name: "Plan & Save",
    path: "/plan-save",
    icon: Target,
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-600",
    description: "Budget and save for your specific goals"
  }
};

// Algorithm to determine category
const determineCategory = (answers) => {
  // Step 1: Check Critical Overrides
  if (answers.q1 === 'A' && answers.q10 !== 'A') {
    return {
      category: 'debtFree',
      confidence: 'Very High',
      reason: 'critical-debt-override',
      overrideUsed: true
    };
  }
  
  if (answers.q10 === 'A') {
    return {
      category: 'planSave',
      confidence: 'Very High',
      reason: 'urgent-expense-override',
      overrideUsed: true
    };
  }
  
  if (answers.q3 === 'D' && answers.q5 === 'A') {
    return {
      category: 'learnFinance',
      confidence: 'Very High',
      reason: 'needs-education-override',
      overrideUsed: true
    };
  }
  
  if (answers.q7 === 'A' && answers.q1 !== 'A') {
    return {
      category: 'planSave',
      confidence: 'High',
      reason: 'unstable-income-override',
      overrideUsed: true
    };
  }
  
  // Step 2: Calculate Scores
  const scores = {
    debtFree: 0,
    startInvesting: 0,
    buildWealth: 0,
    learnFinance: 0,
    planSave: 0
  };
  
  Object.keys(answers).forEach(question => {
    const answer = answers[question];
    const points = SCORING_MATRIX[question]?.[answer] || {};
    
    Object.keys(points).forEach(category => {
      scores[category] += points[category];
    });
  });
  
  // Step 3: Find Winner
  const sortedCategories = Object.entries(scores)
    .sort((a, b) => b[1] - a[1]);
  
  const winner = sortedCategories[0];
  const runnerUp = sortedCategories[1];
  const pointDifference = winner[1] - runnerUp[1];
  
  // Step 4: Determine Confidence
  let confidence;
  if (pointDifference >= 20) confidence = 'Very High';
  else if (pointDifference >= 10) confidence = 'High';
  else if (pointDifference >= 5) confidence = 'Medium';
  else confidence = 'Low';
  
  return {
    category: winner[0],
    confidence,
    scores,
    runnerUp: runnerUp[0],
    pointDifference,
    overrideUsed: false
  };
};

const CategoryQuiz = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [aiReasoning, setAiReasoning] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const progress = ((currentQuestion) / QUESTIONS.length) * 100;
  
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };
  
  const handleNext = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResult();
    }
  };
  
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };
  
  const calculateResult = async () => {
    setIsLoading(true);
    
    // Calculate category using algorithm
    const algorithmResult = determineCategory(answers);
    setResult(algorithmResult);
    
    // Get AI-powered reasoning
    try {
      const response = await fetch(`${API_URL}/api/quiz/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          algorithmResult
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiReasoning(data);
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      // Continue without AI reasoning
    }
    
    setShowResult(true);
    setIsLoading(false);
  };
  
  const currentQ = QUESTIONS[currentQuestion];
  const currentAnswer = answers[currentQ?.id];
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-navy-700 mb-2">
            Analyzing Your Responses...
          </h2>
          <p className="text-slate-600">Our AI is crafting personalized recommendations for you</p>
        </div>
      </div>
    );
  }
  
  if (showResult && result) {
    const categoryInfo = CATEGORY_INFO[result.category];
    const CategoryIcon = categoryInfo.icon;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-4">
                Analysis Complete
              </Badge>
              <h1 className="text-3xl font-heading font-bold text-navy-700 mb-2">
                Your Recommended Path
              </h1>
              <p className="text-slate-600">Based on your responses, here's where you should start</p>
            </div>
            
            {/* Recommended Category Card */}
            <Card className="mb-6 overflow-hidden border-2 border-emerald-200">
              <div className={`bg-gradient-to-r ${categoryInfo.color} p-6 text-white`}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <CategoryIcon className="w-8 h-8" />
                  </div>
                  <div>
                    <Badge className="bg-white/20 text-white border-0 mb-2">
                      {result.confidence} Confidence Match
                    </Badge>
                    <h2 className="text-2xl font-heading font-bold">{categoryInfo.name}</h2>
                    <p className="text-white/80">{categoryInfo.description}</p>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-6">
                {/* AI Reasoning */}
                {aiReasoning?.reasoning ? (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-semibold text-navy-700">Why This Path?</h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed">{aiReasoning.reasoning}</p>
                  </div>
                ) : (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-semibold text-navy-700">Why This Path?</h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed">
                      Based on your financial situation, goals, and current knowledge level, 
                      this category offers the most relevant tools and guidance for your journey.
                    </p>
                  </div>
                )}
                
                {/* Personalized Steps */}
                {aiReasoning?.personalizedSteps && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-navy-700 mb-3">Your First Steps:</h3>
                    <ul className="space-y-2">
                      {aiReasoning.personalizedSteps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-slate-600">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Score Breakdown */}
                {!result.overrideUsed && result.scores && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <h4 className="text-sm font-medium text-slate-500 mb-3">Category Scores</h4>
                    <div className="space-y-2">
                      {Object.entries(result.scores)
                        .sort((a, b) => b[1] - a[1])
                        .map(([cat, score]) => (
                          <div key={cat} className="flex items-center gap-2">
                            <span className="text-xs w-24 truncate text-slate-600">
                              {CATEGORY_INFO[cat]?.name}
                            </span>
                            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${cat === result.category ? 'bg-emerald-500' : 'bg-slate-400'} rounded-full`}
                                style={{ width: `${Math.min((score / 100) * 100, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 w-8">{score}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => navigate(categoryInfo.path)}
                    className={`flex-1 bg-gradient-to-r ${categoryInfo.color} hover:opacity-90 text-white py-6 text-lg rounded-xl`}
                  >
                    Go to {categoryInfo.name}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/tools')}
                    className="flex-1 py-6 text-lg rounded-xl"
                  >
                    Browse All Tools
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Next Category Suggestion */}
            {aiReasoning?.nextCategory && (
              <div className="text-center text-slate-500 text-sm">
                <p>After completing this, consider: <strong>{CATEGORY_INFO[aiReasoning.nextCategory]?.name || aiReasoning.nextCategory}</strong></p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">
            🧭 Find Your Path
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
            Not Sure Where to Start?
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Answer these 10 questions to find the perfect starting point for your financial journey
          </p>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="mb-6">
              <CardContent className="p-6">
                <Badge className="mb-4 bg-slate-100 text-slate-600">
                  {currentQ.title}
                </Badge>
                <h2 className="text-xl font-heading font-bold text-navy-700 mb-6">
                  {currentQ.question}
                </h2>
                
                <div className="space-y-3">
                  {currentQ.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(currentQ.id, option.value)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        currentAnswer === option.value
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                          currentAnswer === option.value
                            ? 'border-emerald-500 bg-emerald-500'
                            : 'border-slate-300'
                        }`}>
                          {currentAnswer === option.value && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className={`text-sm sm:text-base ${
                          currentAnswer === option.value ? 'text-emerald-700 font-medium' : 'text-slate-700'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className="px-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!currentAnswer}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
          >
            {currentQuestion === QUESTIONS.length - 1 ? 'See Results' : 'Next'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        
        {/* Footer Note */}
        <p className="text-center text-slate-500 text-sm mt-8">
          This quiz will help you choose a category to begin with for your investing and wealth building journey!
        </p>
      </div>
    </div>
  );
};

export default CategoryQuiz;
